import React, { useState, useMemo } from 'react';
import { stripCitationMarkers } from '../../utils/citationMarkers';

/**
 * Enhance message formatting with custom elements.
 * Pure string transform — must stay callable during render (see useMemo below).
 */
const enhanceFormatting = (html, messageType) => {
    // Only convert newlines to paragraphs for hjelpestonad_intro messages
    // This prevents excessive spacing in normal AI responses
    if (messageType === 'hjelpestonad_intro') {
      // Convert double newlines to paragraph breaks for intro messages
      html = html.replace(/\n\n+/g, '</p><p class="mb-4">');
      // Then, convert single newlines to line breaks
      html = html.replace(/\n/g, '<br/>');
      // Wrap in paragraph tags if not already wrapped
      if (!html.startsWith('<p')) {
        html = '<p class="mb-4">' + html + '</p>';
      }
    }

    // Add special formatting for key sections
    html = html.replace(/\*\*Neste steg:\*\*/gi, '<div class="info-box"><span class="info-box-icon">📋</span><strong>Neste steg:</strong>');
    html = html.replace(/\*\*Viktig:\*\*/gi, '<div class="warning-box"><span class="info-box-icon">⚠️</span><strong>Viktig:</strong>');
    html = html.replace(/\*\*Du har rett på:\*\*/gi, '<div class="success-box"><span class="info-box-icon">✅</span><strong>Du har rett på:</strong>');

    // Close info boxes
    html = html.replace(/(<div class="(info|warning|success)-box">[\s\S]*?)(\n\n|<\/p><p>|$)/g, '$1</div>$3');

    // Note: Lists are already formatted by MarkdownIt on the backend
    // CSS styles in conversation.css (.ai-message-prose ul li) handle the styling

    // Add section dividers for better readability
    html = html.replace(/---/g, '<div class="section-divider"><span class="section-divider-text">•</span></div>');

    // Highlight key amounts and dates
    html = html.replace(/(\d+\s*kr\b)/gi, '<span class="key-info">$1</span>');
    html = html.replace(/(\d{1,2}\.\s*(januar|februar|mars|april|mai|juni|juli|august|september|oktober|november|desember))/gi, '<span class="key-info">$1</span>');

    return html;
};

/**
 * Enhanced message content formatter
 * Adds visual hierarchy, better spacing, and interactive elements
 */
const EnhancedMessageContent = ({ content, messageType }) => {
  const [expandedSections, setExpandedSections] = useState({});

  // Derived synchronously so a message renders at its final height in its
  // first commit. The previous useEffect+setState pattern rendered every AI
  // message empty for one commit — ConversationView measures scroll geometry
  // in a layout effect during that first commit, so it landed on a collapsed
  // page that then multiplied in height.
  // Kilde markers are stripped from every answer; its sources are behind
  // "Vis kilder" (MessageSources).
  const processedContent = useMemo(
    () => enhanceFormatting(stripCitationMarkers(content), messageType),
    [content, messageType]
  );

  /**
   * Toggle expanded state for a section
   */
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  /**
   * Check if content is long and needs progressive disclosure
   */
  const isLongContent = processedContent.length > 1000;

  return (
    <div className="ai-message-content">
      {isLongContent && !expandedSections.full ? (
        <>
          <div className="content-preview">
            <div
              className="ai-message-prose"
              dangerouslySetInnerHTML={{ __html: processedContent }}
            />
          </div>
          <button
            onClick={() => toggleSection('full')}
            className="expand-button mt-4"
          >
            Les mer
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </>
      ) : (
        <>
          <div
            className="ai-message-prose"
            dangerouslySetInnerHTML={{ __html: processedContent }}
          />
          {isLongContent && expandedSections.full && (
            <button
              onClick={() => toggleSection('full')}
              className="expand-button mt-4"
            >
              Vis mindre
              <svg className="w-4 h-4 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default EnhancedMessageContent;